import {
  AppState,
  Course,
  DownloadBrochureApiResponse,
} from '@jobseeker/common-core/types';
import {
  Box,
  Grid,
  styled,
  Typography,
  TypographyProps,
  useTheme,
} from '@mui/material';
import {
  EnquireNowBtn,
  DownloadBrochureBtn,
  EnquiryType,
} from './EnquiryButtons';
import {useSelector} from 'react-redux';
import dynamic from 'next/dynamic';
import Loader from '@jobseeker/lotus/components/Loader';
import {VersionVariants} from '@jobseeker/lotus/enums/variants';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {memo, useEffect, useState} from 'react';
import {Card} from '@jobseeker/lotus/components';
import ProfileInfoPopup from '@jobseeker/feature-course/components/ProfileInfoPopup';
import {courseService, userService} from '@jobseeker/common-core/services';
import {
  getDeviceOs,
  handleTracking,
  openUrl,
} from '@jobseeker/common-core/utils';
import Header from './Header';
import EnquirySuccessPopup from './EnquirySuccessPopup';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME || '';
const isAppIimjobs = PLATFORM === 'iimjobs';
const isAppHirist = PLATFORM === 'hirist';

const CourseDetailCard = dynamic(
  () => import('@jobseeker/lotus/components/JobDetailCard'),
  {
    loading: () => <Loader />,
  }
);

const AdsPanelHarness = memo(
  dynamic(
    () => import('@jobseeker/client-harness/components/AdsHarness/AdsPanelV2'),
    {
      loading: () => <Loader />,
    }
  )
);

const Heading: React.FC<TypographyProps> = styled(Typography)(({theme}) => ({
  fontWeight: 'bold',
  fontSize: theme.typography.pxToRem(16),
}));

const CourseDetail = (
  props: Course & {
    handleEnquiryInLoggedOutState: () => void;
    forUnpublishedCourse?: boolean;
  }
) => {
  const {
    id,
    fee,
    path,
    title,
    brochure = '',
    category,
    duration,
    location,
    oneLiner,
    introtext,
    institute,
    maxWorkExp,
    minWorkExp,
    refinv = 'NA',
    reforg = 'NA',
    deliveryMethod,
    alreadyEnquired,
    courseHighlight,
    desiredCandProfile,
    forUnpublishedCourse = false,
    handleEnquiryInLoggedOutState,
  } = props;

  const theme = useTheme();

  const {
    userId = '0',
    authInfo: {token = ''},
  } = useSelector((state: AppState) => state.auth);

  const {
    profile: {
      locations: {current: {id: userCurrentLocationId = 0}} = {
        current: {id: 0},
      },
      experienceYear = -1,
      phone = '0',
    },
  } = useSelector((state: AppState) => state.user) as {
    profile: {
      locations: {current: {id: number}};
      experienceYear: number;
      phone: string;
    };
  };

  const isDeviceLarge = useIsLargeDevice(1024);

  const [courseEnquired] = useState(false);
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [profileInfoUpdated, setProfileInfoUpdated] = useState(false);
  const [enquiryType, setEnquiryType] = useState<EnquiryType>(
    EnquiryType.Enquire
  );
  const [countriesJson, setCountriesJson] = useState([
    {countryCode: '', countryName: '', countryCodeAlpha: ''},
  ]);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showEnquirySuccessPopup, setShowEnquirySuccessPopup] = useState(false);
  const [enquiryId, setEnquiryId] = useState(-1);
  const [userAgent, setUserAgent] = useState<string>('');

  useEffect(() => {
    if (typeof navigator !== 'undefined') {
      setUserAgent(navigator.userAgent || navigator.vendor || '');
    }
  }, []);

  useEffect(() => {
    if (!profileInfoUpdated) {
      const fetchCountries = async () => {
        try {
          const response = await userService.getS3Resource('countrycodes');
          setCountriesJson(response.countrycodes);
        } catch (error) {
          console.error('Failed to fetch country data', error);
        }
      };
      fetchCountries();

      return;
    }

    setShowEnquiryPopup(false);

    if (enquiryType === EnquiryType.DownloadBrochure) {
      downloadBrochure();
    } else {
      enquireCourse();
    }
  }, [profileInfoUpdated, enquiryType]);

  const closeEnquirySuccessPopup = () => {
    if (isAppHirist) {
      setShowEnquirySuccessPopup(false);
    }
  };

  const closeEnquiryPopup = () => {
    setShowEnquiryPopup(false);
  };

  const handleProfileUpdate = () => {
    setProfileInfoUpdated(true);
  };

  const downloadBrochure = async () => {
    handleTracking('lgClickCourseDownload', {
      event_category: 'Learn and Grow',
      CourseId: `${id}`,
      UserId: `${userId}`,
      Status: `${userId ? 'LoggedIn' : 'LoggedOut'}`,
      Device: `${getDeviceOs(userAgent)}`,
      Reforg: reforg,
      RefInv: refinv,
    });

    const downloadBrochureApiResponse: DownloadBrochureApiResponse =
      await courseService.downloadBrochure({
        coursePublishedId: id,
        token,
        refinv,
        reforg,
      });

    if (downloadBrochureApiResponse?.brochure_file) {
      openUrl(downloadBrochureApiResponse.brochure_file, '_blank');
    }
  };

  const enquireCourse = async () => {
    const response = await courseService.enquireCourse({
      coursePublishedId: id,
      token: token || '',
      refinv,
      reforg,
    });

    if (response?.error) {
      throw new Error(response.error.message);
    }

    if (isAppHirist) {
      setEnquiryId(response?.enquiryID ?? -1);
      setShowEnquirySuccessPopup(true);
    }

    if (isAppIimjobs) {
      const queryParamsString = new URLSearchParams({
        refinv,
        reforg,
        id: id.toString(),
        enq: `${response?.enquiryID ?? -1}`,
        userLocation: userCurrentLocationId.toString(),
        exp: experienceYear.toString(),
        category: (category?.[0].id ?? 0).toString(),
      });

      setTimeout(
        openUrl,
        1000,
        `/similar-course?${queryParamsString}`,
        '_self'
      );
    }
  };

  const handleEnquiryButtonClick = async (type: EnquiryType) => {
    if (isAppHirist) {
      setButtonLoading(true);
    }

    if (!token?.length) {
      setButtonLoading(false);
      handleEnquiryInLoggedOutState();
      return;
    }

    setEnquiryType(type);

    if (
      !profileInfoUpdated &&
      (phone === '0' || experienceYear === -1 || userCurrentLocationId === 0)
    ) {
      setButtonLoading(false);
      setShowEnquiryPopup(true);
      return;
    }

    if (token.length > 0) {
      if (type === EnquiryType.DownloadBrochure) {
        await downloadBrochure();
      } else {
        await enquireCourse();
      }

      // setCourseEnquired(true);
    }

    if (isAppHirist) {
      setButtonLoading(false);
    }
  };

  const EnquiryButtons = () => {
    return (
      <>
        <Grid
          container
          gap={2}
          sx={{
            ...(!isDeviceLarge && {
              justifyContent: 'space-between',
              columnGap: 0.5,
            }),
          }}
        >
          <Grid item xs={5.75} md={12}>
            <DownloadBrochureBtn
              handleEnquiryButtonClick={handleEnquiryButtonClick}
              isDeviceLarge={isDeviceLarge}
            />
          </Grid>
          <Grid item xs={5.75} md={12}>
            <EnquireNowBtn
              disabled={!!alreadyEnquired || courseEnquired}
              text={
                !!alreadyEnquired || courseEnquired ? 'Enquired' : 'Enquire Now'
              }
              handleEnquiryButtonClick={handleEnquiryButtonClick}
              isDeviceLarge={isDeviceLarge}
            />
          </Grid>
        </Grid>
        <EnquireNowBtn
          disabled={!!alreadyEnquired || courseEnquired}
          text={'I am interested'}
          handleEnquiryButtonClick={handleEnquiryButtonClick}
          isDeviceLarge={isDeviceLarge}
        />
      </>
    );
  };

  return (
    <>
      {showEnquiryPopup && (
        <ProfileInfoPopup
          userId={parseInt(userId, 10)}
          closeEnquiryPopup={closeEnquiryPopup}
          handleProfileUpdate={handleProfileUpdate}
          countriesJson={countriesJson}
        />
      )}
      {showEnquirySuccessPopup && (
        <EnquirySuccessPopup
          closePopup={closeEnquirySuccessPopup}
          enquiredCourseId={id}
          enquiryId={enquiryId}
          setdialogOpen={setShowEnquirySuccessPopup}
        />
      )}
      {isAppHirist && (
        <Header
          courseInfo={props}
          buttonLoading={buttonLoading}
          courseEnquired={courseEnquired}
          handleEnquiryButtonClick={handleEnquiryButtonClick}
          forUnpublishedCourse={forUnpublishedCourse}
        />
      )}
      {(isAppIimjobs || (isAppHirist && isDeviceLarge)) && (
        <Grid
          container
          gap={isAppIimjobs ? 2 : isAppHirist ? 6 : 0}
          sx={isAppHirist ? {px: {xxs: 0, lg: 6}, pt: 8, mb: 0} : {}}
        >
          <Box
            sx={
              isDeviceLarge
                ? {
                    width: isAppIimjobs
                      ? 'calc(0.832 * (100% - 1024px) + 696px)'
                      : 'calc(0.632 * (100% - 1024px) + 742px)',
                  }
                : isAppHirist
                ? {maxHeight: 'fit-content'}
                : {}
            }
          >
            {isAppIimjobs && (
              <>
                <CourseDetailCard
                  title={title}
                  variant={VersionVariants.VERSION_TWO}
                  location={
                    typeof location === 'string'
                      ? location
                      : location
                          ?.filter(({name}) => name?.length)
                          ?.map(({name}) => name)
                          .join('/') || 'Remote'
                  }
                  companyName={institute}
                  experiance={`${minWorkExp} - ${maxWorkExp} yrs`}
                  date={Date.now()}
                  category={{label: '', tag: ''}}
                  companyImgUrl={path}
                  isPremiumJob={false}
                  isCompanyJob={false}
                  isDetailCard
                  whatFor="course-detail"
                  noShadow
                />
                <Box
                  bgcolor={theme.palette.primary.contrastText}
                  py={3}
                  px={2}
                  height={2}
                  zIndex={100}
                  position="relative"
                  top={-10}
                />
                <Box
                  bgcolor={theme.palette.primary.contrastText}
                  py={2}
                  pl={isDeviceLarge ? 10 : 1.5}
                  pr={1.5}
                  zIndex={100}
                  position="relative"
                  top={-45}
                  borderTop={`1px solid ${theme.palette.border.lightGray}`}
                  borderRadius="0 0 16px 16px"
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    maxWidth={600}
                    flexWrap="wrap"
                    rowGap={2}
                  >
                    <Box>
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                      >
                        Fee
                      </Typography>
                      <Typography fontSize={14} fontWeight={500}>
                        {fee}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                      >
                        Duration
                      </Typography>
                      <Typography fontSize={14} fontWeight={500}>
                        {duration}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                      >
                        Experience
                      </Typography>
                      <Typography fontSize={14} fontWeight={500}>
                        {minWorkExp} - {maxWorkExp} yrs
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        fontSize={14}
                        fontWeight={500}
                        color={theme.palette.text.secondary}
                      >
                        Delivery Method
                      </Typography>
                      <Typography fontSize={14} fontWeight={500}>
                        {deliveryMethod}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </>
            )}
            <Box
              bgcolor={theme.palette.primary.contrastText}
              mt={2.25}
              py={isAppIimjobs ? 3 : 2.5}
              px={isAppIimjobs ? 2 : 2.5}
              borderRadius="16px"
              position="relative"
              top={-45}
              sx={
                isAppHirist
                  ? {
                      borderRadius: {xs: '15px', md: '8px'},
                      '& p': {
                        margin: '7px 0px',
                        fontSize: 16,
                        textAlign: 'justify',
                        lineHeight: '26px',
                        color: '#000',
                      },
                      '& b': {
                        color: '#000',
                        textAlign: 'justify',
                        fontSize: 18,
                        fontWeight: 'bold',
                        display: 'inline-block',
                      },
                      '& ul': {
                        listStyle: 'disc',
                        padding: '0px 25px',
                      },
                      '& li': {
                        fontSize: 16,
                        lineHeight: '20px',
                        marginRight: 0.5,
                      },
                    }
                  : {}
              }
            >
              {isAppIimjobs && (
                <Typography component="q" fontSize={14}>
                  {oneLiner}
                </Typography>
              )}

              <Box mt={isAppIimjobs ? 2 : 0}>
                {isAppIimjobs && (
                  <Heading component="h2">Course Detail</Heading>
                )}
                <div
                  dangerouslySetInnerHTML={{__html: introtext}}
                  style={{
                    fontSize: '14px',
                  }}
                />
              </Box>

              <Box mt={4}>
                <Heading component="h2">Desired Candidate Profile</Heading>
                <div
                  dangerouslySetInnerHTML={{__html: desiredCandProfile}}
                  style={{
                    fontSize: '14px',
                  }}
                />
              </Box>

              <Box mt={4}>
                <Heading component="h2">Course Modules</Heading>
                <div
                  dangerouslySetInnerHTML={{__html: courseHighlight}}
                  style={{
                    fontSize: '14px',
                  }}
                />
              </Box>
            </Box>
          </Box>

          {isAppHirist && isDeviceLarge && (
            <Box
              maxWidth={352}
              width="calc(100% - 0.632 * (100% - 1024px) - 790px)"
              position="relative"
              top="-30px"
            >
              <AdsPanelHarness
                variant={VersionVariants.VERSION_TWO}
                showSmallBadges={false}
                showFeatured={false}
                showLearning={true}
                showLearningCenterCTA={false}
                showLearnGrow={true}
              />
            </Box>
          )}

          {isAppIimjobs && !forUnpublishedCourse && (
            <Card
              variant=""
              heading=""
              sx={{
                p: 2,
                position: 'sticky',
                height: '100%',
                ...(isDeviceLarge && {top: 80}),
                maxWidth: 366,
                width: 'calc(100% - 0.832 * (100% - 1024px) - 720px)',
                px: 3.5,
                py: 3,
                ml: 1,
                ...(!isDeviceLarge && {
                  bottom: 0,
                  maxWidth: '100%',
                  width: '100%',
                  left: 0,
                  ml: 0,
                  height: 'fit-content',
                  borderRadius: 'none',
                  position: 'fixed',
                  zIndex: 12,
                  boxShadow: 'inset 0 1px 8px rgba(0, 0, 0, 0.2)',
                }),
              }}
            >
              <Box
                display="flex"
                flexDirection={isDeviceLarge ? 'column' : 'row'}
                gap={2}
                sx={
                  isDeviceLarge
                    ? {alignItems: 'center'}
                    : {flexDirection: 'column-reverse'}
                }
              >
                <EnquiryButtons />

                {isDeviceLarge && (
                  <Typography
                    maxWidth={218}
                    fontSize={theme.typography.pxToRem(14)}
                    textAlign="center"
                    justifySelf="center"
                    color={theme.palette.text.secondary}
                    fontWeight={500}
                    lineHeight={theme.typography.pxToRem(15)}
                  >
                    You may be contacted by the course provider
                  </Typography>
                )}
              </Box>
            </Card>
          )}
        </Grid>
      )}

      {isAppHirist && !forUnpublishedCourse && (
        <Box
          px={{xxs: 3, lg: 6}}
          display="flex"
          flexDirection={isDeviceLarge ? 'row' : 'column'}
          gap={isDeviceLarge ? 3 : 2}
          bgcolor="white"
          py={2.5}
          position="sticky"
          bottom={0}
          zIndex={1}
        >
          <EnquireNowBtn
            text={
              Boolean(alreadyEnquired) || courseEnquired
                ? 'Enquired'
                : 'Enquire Now'
            }
            sx={{
              bgcolor: `${theme.palette.primary.main} !important`,
              color: `${theme.palette.primary.contrastText} !important`,
              borderRadius: '4px',
              height: 48,
              width: 220,
              fontSize: theme.typography.pxToRem(16),
              fontWeight: 400,
              ...(!isDeviceLarge && {
                ml: 'auto',
                mr: 'auto',
                textAlign: 'center',
              }),
            }}
            disabled={Boolean(alreadyEnquired) || courseEnquired}
            buttonLoading={buttonLoading}
            handleEnquiryButtonClick={handleEnquiryButtonClick}
          />
          {brochure.length > 0 && (
            <Box display="flex" flexDirection="column">
              <DownloadBrochureBtn
                sx={{
                  bgcolor: `${theme.palette.primary.contrastText} !important`,
                  color: `${theme.palette.text.primary} !important`,
                  fontSize: theme.typography.pxToRem(16),
                  fontWeight: 400,
                  borderRadius: '4px',
                  height: 48,
                  width: 220,
                  border: 'none',
                  boxShadow: 'rgba(0, 0, 0, 0.39) 0px 4px 11px 0px !important',
                  ...(!isDeviceLarge && {
                    ml: 'auto',
                    mr: 'auto',
                    textAlign: 'center',
                  }),
                }}
                // buttonLoading={buttonLoading}
                handleEnquiryButtonClick={handleEnquiryButtonClick}
              />
              <Box mt={1} textAlign="center">
                <Typography
                  fontSize={theme.typography.pxToRem(10)}
                  fontWeight={300}
                  lineHeight="12px"
                  color={theme.palette.secondary.dark}
                >
                  You may be contacted by the course provider
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </>
  );
};

export default CourseDetail;
